import '../../../components/text-and-lang/as-text.ts';
import '../../../components/base/as-op-status.ts';
import type { AsOpStatus } from '../../../components/base/as-op-status.ts';
import type { ExportStatusDetail } from '../../../data/data-interfaces.external.ts';
import buttonStyleText from '../../../components/base/buttons/as-button.css?raw';
import { AsButtonBase } from '../../../components/base/buttons/as-button-base.ts';

/**
 * <as-grabcad-export-button>
 *
 * Self-contained GrabCAD send button. Fires as-button-click synchronously on
 * click (giving parents a chance to configure the exporter), then calls
 * exporter.sendToGrabCAD(). Status is driven by as-export-status events emitted
 * by the grabcad plugin, displayed via an internal as-op-status.
 *
 * Attributes
 *   default-text   Button label
 *   dict-key       Localization key for label
 *   name           Passed in as-button-click detail
 *   package-name   Passed to exporter.sendToGrabCAD({ packageName })
 *   exporter-id    Which exporter to listen to (default: 'default')
 *   disabled       Disables the button
 *
 * Events
 *   as-button-click  bubbles, composed — fired synchronously before the async send
 */
export class AsGrabcadExportButton extends AsButtonBase {
  static readonly observedAttributes = [
    'name', 'default-text', 'dict-key', 'pending-default-text', 'pending-dict-key',
    'package-name', 'exporter-id', 'disabled',
  ];

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(
      buttonStyleText + `
:host {
  align-items: center;
  gap: 0.25rem;
}
as-op-status { flex-shrink: 0; }`
    );
    return s;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, AsGrabcadExportButton._sheet];
    this._render();
  }

  connectedCallback(): void {
    this._attachListeners();
    window.addEventListener('as-export-status', this._onExportStatus);
    window.addEventListener('as-custom-data-changed', this._reset);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-export-status', this._onExportStatus);
    window.removeEventListener('as-custom-data-changed', this._reset);
  }

  attributeChangedCallback(_name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    this._render();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  reset(): void { this._reset(); }

  // ── Private ────────────────────────────────────────────────────────────────

  #pending = false;

  private get _status(): AsOpStatus | null {
    return this.shadowRoot!.querySelector('as-op-status');
  }

  private _reset = (): void => {
    this.#pending = false;
    this._updateButtonState();
    this._status?.reset();
  };

  private _onExportStatus = (e: Event): void => {
    const { exporterId, operation, state, message } = (e as CustomEvent<ExportStatusDetail>).detail;
    if (exporterId !== (this.getAttribute('exporter-id') ?? 'default')) return;
    if (operation !== 'grabcad') return;

    this.#pending = state === 'pending';
    this._updateButtonState();

    switch (state) {
      case 'pending': this._status?.setPending(); break;
      case 'success': this._status?.setSuccess('Transfer Success'); break;
      case 'error':   this._status?.setError(message); break;
    }
  };

  /** Re-renders the button's inner content only — as-op-status is a sibling and is untouched. */
  private _updateButtonState(): void {
    const btn = this.shadowRoot?.querySelector('button');
    if (!btn) return;
    btn.disabled = this.#pending || this.hasAttribute('disabled');

    const text    = this.#pending ? (this.getAttribute('pending-default-text') ?? 'Sending...') : (this.getAttribute('default-text') ?? '');
    const dictKey = this.#pending ? this.getAttribute('pending-dict-key') : this.getAttribute('dict-key');
    btn.innerHTML = dictKey
      ? `<as-text dict-key="${dictKey}" default-text="${text}"></as-text>`
      : `<as-text>${text}</as-text>`;
  }

  private _render(): void {
    const text     = this.getAttribute('default-text') ?? '';
    const dictKey  = this.getAttribute('dict-key');
    const disabled = this.hasAttribute('disabled');
    const textHtml = dictKey
      ? `<as-text dict-key="${dictKey}" default-text="${text}"></as-text>`
      : `<as-text>${text}</as-text>`;

    const statusTooltip = this.getAttribute('default-text') ?? 'Send to GrabCAD';
    this.shadowRoot!.innerHTML =
      `<button${disabled ? ' disabled' : ''}>${textHtml}</button><as-op-status tooltip-default-text="${statusTooltip}"></as-op-status>`;
    this._attachListeners();
  }

  private _attachListeners(): void {
    const btn = this.shadowRoot?.querySelector('button');
    if (!btn) return;
    btn.removeEventListener('click', this._handleClick);
    btn.addEventListener('click', this._handleClick);
  }

  private _handleClick = async (): Promise<void> => {
    // Fire synchronously — parent can configure the exporter in this handler
    // before the async send begins.
    this._dispatchButtonClick({ buttonName: this.getAttribute('name') });

    const exporter = (document.querySelector('as-app') as any)?.exporter;
    if (!exporter) return;

    // Status updates arrive via as-export-status events emitted by the grabcad plugin.
    await exporter.sendToGrabCAD({ packageName: this.getAttribute('package-name') ?? undefined })
      .catch(() => { /* error state shown via as-export-status */ });
  };
}

if (!customElements.get('as-grabcad-export-button')) {
  customElements.define('as-grabcad-export-button', AsGrabcadExportButton);
} else {
  console.warn('as-grabcad-export-button already defined');
}
